import type { Matchday, Round, Team, SwiperBreakpoints } from '@uefadigital/panenka';
import { MatchDayType } from '@uefadigital/panenka';
import { PkButton, PkCarousel, PkChip, PkDivider, PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import i18n from 'i18next';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCompetitionStructureQuery, useGetTeamsFromCompetitionQuery } from 'api/fspApi';
import { ClubFilter } from 'components/ClubFilter';
import { MatchUnitDay } from 'components/MatchUnitDay';
import { NearlyDoneModal } from 'components/NearlyDoneModal';
import { TeamModal } from 'components/TeamModal';
import useIsDesktop from 'hooks/useIsDesktop';
import config from 'utils/config';
import { translateEntity } from 'utils/translateEntity';

import * as style from './style.module.css';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export interface Fixtures {
  name: string;
  dateTo?: string;
  dateFrom?: string;
  selected: boolean;
  roundId?: string;
  matchDayType?: MatchDayType;
  id: Array<string>;
}
export interface IFixturesAndResults {
  competitionId: string;
  competitionName: string;
  competitionShortName: string;
  seasonYear: string;
  competitionLogo: string;
  competitionType: string;
}

const breakpoints: SwiperBreakpoints = {
  0: { slidesPerView: 'auto', spaceBetween: 12, centeredSlides: true, centeredSlidesBounds: true },
  250: { spaceBetween: 24, centeredSlides: true, centeredSlidesBounds: true },
};

enum CompareDateType {
  FROM = 'FROM',
  TO = 'TO',
}

const compareDate = (start: string | undefined, end: string | undefined, compareDateType: CompareDateType) => {
  if (start === undefined) {
    return end;
  }

  if (end === undefined) {
    return start;
  }

  const startDate = new Date(start);
  const endDate = new Date(end);

  if (compareDateType === CompareDateType.FROM) {
    return dayjs(startDate).isAfter(endDate) ? end : start;
  }

  return dayjs(startDate).isBefore(endDate) ? end : start;
};

const sortByTabDate = (a: Matchday, b: Matchday) => dayjs(a.dateFrom).diff(b.dateFrom);

export const FixturesAndResults = memo(
  ({
    competitionId,
    competitionShortName,
    competitionName,
    seasonYear,
    competitionLogo,
    // competitionType,
  }: IFixturesAndResults) => {
    const { t } = useTranslation();
    const isDesktop = useIsDesktop();
    const ACTIVE = 'ACTIVE';

    const competitionConf =
      config.competitions.find((item) => item.name === competitionName) || config.competitionsDefault;

    const rounds = competitionConf?.displayRounds?.split(',').map((e) => e.trim());

    const [currentActive, setCurrentActive] = useState<number | null>(null);
    const pkCarouselElement = useRef<HTMLPkCarouselElement>(null);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [teamIds, setTeamIds] = useState<Array<string>>([]);
    const [modalNearlyDoneIsOpen, setModalNearlyDoneIsOpen] = useState(false);

    const [displayFilter, setDisplayOpen] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState<undefined | Team>(undefined);
    const [currentMatchDayId, setCurrentMatchDayId] = useState<undefined | Array<string>>(undefined);

    const params = selectedTeam
      ? { teamId: selectedTeam.id, seasonYear, competitionId, phase: competitionConf.phase ?? 'ALL' }
      : { matchDayId: currentMatchDayId };

    const {
      data: competition = [],
      error: competitionIsError,
      isLoading,
    } = useGetCompetitionStructureQuery({
      id: competitionId,
      seasonYear,
    });

    const closeModal = () => {
      setIsOpen(false);
    };

    const closeNearlyDoneModal = () => {
      setModalNearlyDoneIsOpen(false);
      setIsOpen(false);
    };

    const openNearlyDoneModal = (teams: Array<string>) => {
      setTeamIds(teams);
      setIsOpen(false);
      setModalNearlyDoneIsOpen(true);
    };

    const getDateFormatted = (startDate: string | undefined, endDate: string | undefined) => {
      if (endDate === undefined) {
        return '';
      }

      const end = new Date(endDate);

      if (startDate === undefined) {
        return end.toLocaleDateString(i18n.language, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }

      const start = new Date(startDate);

      if (
        start.getDay() === end.getDay() &&
        start.getMonth() === end.getMonth() &&
        start.getFullYear() === end.getFullYear()
      ) {
        return start.toLocaleDateString(i18n.language, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }

      if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return start.toLocaleDateString(i18n.language, {
          month: 'long',
          year: 'numeric',
        });
      }

      if (start.getMonth() !== end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return `${start.toLocaleDateString(i18n.language, { month: 'long' })}-${end.toLocaleDateString(i18n.language, {
          month: 'long',
          year: 'numeric',
        })}`;
      }

      return start.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };

    const matchdaysSorted = useMemo(
      () =>
        [...competition[0].matchdays].sort((a, b) => {
          return sortByTabDate(a, b) || (a.sequenceNumber?.localeCompare(b.sequenceNumber ?? '0') ?? 0);
        }),
      [competition],
    );

    const matchdaysByMonth = useMemo(() => {
      const listDate = [...competition[0].matchdays].map((md) => dayjs(md.dateFrom)).sort((a, b) => dayjs(a).diff(b));

      return [...new Set(listDate.map((item) => item.format('MMMM YYYY')))];
    }, [competition]);

    const matchDaysInMonth = useCallback(
      (month: Dayjs) => {
        const start = month.startOf('month').format('YYYY-MM-DD');
        const end = month.endOf('month').format('YYYY-MM-DD');

        const listDate = [...competition[0].matchdays]
          .filter(
            (md) => md.dateFrom && dayjs(md.dateFrom).isSameOrAfter(start) && dayjs(md.dateTo).isSameOrBefore(end),
          )
          .map((md) => md.id);

        return [...new Set(listDate)] as string[];
      },
      [competition],
    );

    const currentComp = config.competitions.find((c) => c.id === competitionId);
    const isGroupByMonth = currentComp?.groupByMonth;
    const hideMatchUnitDetail = currentComp?.hideMatchUnitDetail ?? false;

    const isNotPreliminaryRound = useCallback(
      (type: string | undefined) => {
        return competitionId === '19' || competitionId === '2014' || (type && rounds?.includes(type));
      },
      [competitionId, rounds],
    );

    const fixtures = useMemo<Fixtures[]>(() => {
      const result: Fixtures[] = [];
      if (isGroupByMonth) {
        matchdaysByMonth.map((month) => {
          const dayjsMonth = dayjs(month, 'MMMM YYYY');
          const formattedMonth = dayjsMonth.toDate().toLocaleDateString(i18n.language, {
            month: 'long',
          });

          result.push({
            name: formattedMonth.charAt(0).toUpperCase() + formattedMonth.slice(1),
            dateFrom: dayjsMonth.startOf('month').format('YYYY-MM-DD'),
            dateTo: dayjsMonth.endOf('month').format('YYYY-MM-DD'),
            selected: dayjs().isSame(dayjsMonth, 'month'),
            matchDayType: 'FRIENDLY' as MatchDayType,
            id: matchDaysInMonth(dayjsMonth),
          });
        });
      } else {
        competition[0].rounds
          .filter((round: Round) => {
            return isNotPreliminaryRound(round.metaData?.type);
          })
          .map((round: Round) => {
            matchdaysSorted.map((matchday: Matchday) => {
              if (round.id === matchday.roundId) {
                let roundName = translateEntity(round)?.name;
                if (round.mode === 'GROUP') {
                  roundName = `${translateEntity(round)?.name} - ${translateEntity(matchday)?.longName}`;
                } else if (round.mode === 'KNOCK_OUT' && round.modeDetail === 'KNOCK_OUT_TWO_LEGS') {
                  const sequenceNumber = matchday.type === MatchDayType.FIRST_LEG ? 1 : 2;

                  roundName = `${translateEntity(round)?.shortName} - ${t(`${sequenceNumber}_leg`)}`;
                }

                const matchdayDateFrom = matchday.dateFrom ?? round.dateFrom;
                const matchdayDateTo = matchday.dateTo ?? round.dateTo;
                const matchdayId = [matchday.id];

                const roundIndex = result.findIndex(
                  (fixture) =>
                    fixture.roundId === round.id &&
                    fixture.matchDayType === matchday.type &&
                    fixture.name === roundName,
                );

                if (roundIndex > 0) {
                  const currentRound = result[roundIndex];

                  currentRound.id.push(matchday.id);
                  currentRound.dateFrom = compareDate(currentRound.dateFrom, matchdayDateFrom, CompareDateType.FROM);
                  currentRound.dateTo = compareDate(currentRound.dateTo, matchdayDateTo, CompareDateType.TO);
                  return;
                }

                result.push({
                  name: roundName ?? '',
                  dateFrom: matchdayDateFrom,
                  dateTo: matchdayDateTo,
                  selected: round.active && matchday.status === ACTIVE,
                  roundId: round.id,
                  matchDayType: matchday.type,
                  id: matchdayId,
                });
              }
            });
          });
      }
      return result;
    }, [competition, isGroupByMonth, isNotPreliminaryRound, matchDaysInMonth, matchdaysByMonth, matchdaysSorted, t]);

    const changeSlider = useCallback(
      (id: number) => {
        setCurrentActive(id === -1 ? 0 : id);

        const requiredMatchDays = competition[0].matchdays.filter((match) =>
          fixtures[id === -1 ? 0 : id]?.id.includes(match.id),
        );

        if (requiredMatchDays) {
          setCurrentMatchDayId(requiredMatchDays.map((matchday) => matchday.id));
        }
      },
      [competition, fixtures],
    );

    useEffect(() => {
      if (currentActive === null) return;

      pkCarouselElement.current?.getSwiperInstance().then((value) => {
        value.slideTo(currentActive);
      });
    }, [currentActive]);

    useEffect(() => {
      if (currentActive !== null) return;

      const currentDate = dayjs();
      const isActive = competition[0].matchdays.find((match) => match.status === ACTIVE);

      [...competition[0].matchdays]
        .sort((match, nextMatch) => {
          return dayjs(nextMatch.dateFrom).diff(match.dateFrom);
        })
        .reverse()
        .forEach((matchDay: Matchday, index: number) => {
          if (matchDay.status === ACTIVE || currentActive === index) {
            const matchDayIndex = isGroupByMonth
              ? fixtures.findIndex((fixture, i) => (fixture.selected ? i : 0))
              : fixtures.findIndex((fixture) => fixture.id.includes(matchDay.id));

            return changeSlider(matchDayIndex);
          } else if (!isActive && dayjs(matchDay?.dateTo).isBefore(currentDate) && matchDay.type === 'FINAL') {
            const matchDayIndex = fixtures.findIndex((fixture, idx) => {
              if (idx === fixtures.length - 1) {
                return fixture.name === 'Final';
              }
            });

            return changeSlider(matchDayIndex);
          }
        });
    }, [changeSlider, competition, currentActive, fixtures, isGroupByMonth]);

    const { data: teams = [] } = useGetTeamsFromCompetitionQuery({ id: competitionId, seasonYear });
    const selectTeam = (el: Team | undefined): void => {
      setSelectedTeam(el);
      setDisplayOpen(false);
    };

    const sortedTeams = [...teams].sort((team, nextTeam) =>
      team?.internationalName?.localeCompare(nextTeam?.internationalName),
    );

    if (isLoading) {
      return <div />;
    }

    if (!competition || !!competitionIsError) {
      return <div className="pk-text--center">{t('no_records')}</div>;
    }

    return (
      <div className="pk-container pk-pt--m pk-pt-md--xl2">
        <div className="pk-col pk-col--span-12 pk-mb--l pk-mb-md--xl">
          <h1 className="pk-font-size--xl2 pk-font-size-md--xl4 pk-line-height--sm pk-font-style--normal pk-text--text-01 pk-font--comp-bold">
            {t('information_kits')}
          </h1>
        </div>
        <div className="pk-col pk-col--span-12 pk-d--flex pk-gap--xs pk-justify-content--between">
          <div className={style.buttonWrapper}>
            <PkButton
              appearance="outlined"
              onPkClick={() => {
                setIsOpen(true);
              }}
            >
              <PkIcon name="actions-add-to-calendar" size={20} class="pk-mr--xs" />{' '}
              <span className="pk-font-size--s">{t('add_to_calendar')}</span>
            </PkButton>
          </div>
          <div className={style.buttonWrapper}>
            <div className="pk-d-sm--none pk-w--100">
              <PkButton appearance="outlined" onPkClick={() => setDisplayOpen(!displayFilter)}>
                <PkIcon name="actions-filter" /> {t('filter')}
              </PkButton>
            </div>
            <div className="pk-d--none pk-d-md--block pk-d-sm--block">
              <PkIcon name="actions-filter" class="pk-d--inline-block pk-align--middle" />
              <span className="pk-mr--xs pk-font-weight--normal pk-text--primary-01 pk-font-size--s pk-font--base-regular">
                {' '}
                {t('filter_by')}
              </span>
              <PkButton appearance="outlined" width="198" onPkClick={() => setDisplayOpen(!displayFilter)}>
                <span className=" pk-font-size--s">{selectedTeam ? selectedTeam.internationalName : t('team')}</span>{' '}
                <PkIcon name={displayFilter ? 'navigation-chevron-up' : 'navigation-chevron-down'} />
              </PkButton>
            </div>
          </div>
        </div>
        <div className={clsx('pk-col pk-col--span-12', displayFilter ? 'pk-d--block' : 'pk-d--none')}>
          <PkDivider class="divider--horizontal" direction="horizontal" />
          <div className={clsx(selectedTeam ? 'pk-d--block' : 'pk-d--none', 'pk-mt--m pk-mb--l pk-text--center')}>
            <PkButton appearance="text" onClick={() => selectTeam(undefined)}>
              <PkIcon name="navigation-close" size={20} />
              <span className="pk-font-weight--light pk-font-size--s">{t('clear_my_selection')}</span>
            </PkButton>
          </div>
          <ClubFilter teams={sortedTeams} callBack={selectTeam} />
        </div>
        <div className={clsx('pk-col pk-col--span-12', selectedTeam ? 'pk-d--none' : 'pk-d--block')}>
          <PkCarousel ref={pkCarouselElement} breakpoints={breakpoints} autoHeight boxShadowPadding>
            {fixtures.map((matchDay, id) => {
              const isSelected = currentActive === id;

              return (
                <PkChip
                  className={style.chip}
                  selected={isSelected}
                  key={`slide-${matchDay.id}`}
                  onClick={() => changeSlider(id)}
                >
                  <span className={clsx('pk-font--base-regular pk-font-size--s', !isSelected && 'pk-text--text-02')}>
                    {matchDay.name}
                  </span>
                  <span slot="text-secondary" className="pk-font-size--xs pk-text--text-03">
                    {matchDay.matchDayType === ('FRIENDLY' as MatchDayType)
                      ? dayjs(matchDay.dateFrom).format('YYYY')
                      : getDateFormatted(matchDay.dateFrom, matchDay.dateTo)}
                  </span>
                </PkChip>
              );
            })}
          </PkCarousel>
        </div>
        <div className={clsx('pk-col pk-col--span-12 pk-mb--xl2', selectedTeam && 'pk-pt--xl')}>
          <PkDivider
            className={clsx('divider--horizontal', selectedTeam ? 'pk-d--none' : 'pk-d--block')}
            direction="horizontal"
          />
          <span
            className={clsx(
              isDesktop ? 'pk-line-height--lg' : 'pk-line-height--md',
              'pk-font-size--s pk-font--base-regular',
            )}
          >
            {t('match_kick_offs_are_listed_in_your_local_time')}
          </span>
        </div>
        <TeamModal
          isOpen={modalIsOpen}
          setClose={closeModal}
          openNearlyDoneModal={openNearlyDoneModal}
          competitionId={competitionId}
          competitionShortName={competitionShortName}
          competitionName={competitionName}
          seasonYear={seasonYear}
          competitionLogoName={competitionLogo}
        />
        <NearlyDoneModal
          isOpen={modalNearlyDoneIsOpen}
          setClose={closeNearlyDoneModal}
          competitionShortName={competitionShortName}
          competitionId={competitionId}
          teamId={teamIds}
        />
        <div className="pk-col pk-col--span-12 pk-mb--xl">
          <MatchUnitDay params={params} isFixturesPage={true} hideMatchUnitDetail={hideMatchUnitDetail} />
        </div>
      </div>
    );
  },
);
FixturesAndResults.displayName = 'FixturesAndResults';
